import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import FilterChip from 'LuxKit/Chips/FilterChip'
import noop from 'lib/function/noop'

const PillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

const StyledFilterChip = styled(FilterChip)`
  &:disabled {
    cursor: default;
  }

  &.selected:disabled {
    --chip-stroke-color: ${props => props.theme.palette.neutral.default.one};
    color: ${props => props.theme.palette.neutral.default.one};
  }
`

export interface PillOption {
  label: string
  action?: string
}

interface Props {
  options: Array<PillOption>
  onSelectionChange?: (selectedLabels: Array<string>) => void
  onAction?: (action: string) => void
  select: 'single' | 'multiple'
  disabled?: boolean
  selection?: Array<string>
}

function PillsList({ options, onSelectionChange = noop, onAction, select, disabled = false, selection }: Props) {
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(() => {
    const initialIndices = new Set<number>()
    if (selection) {
      selection.forEach(label => {
        const index = options.findIndex(opt => opt.label === label)
        if (index !== -1) {
          initialIndices.add(index)
        }
      })
    }
    return initialIndices
  })
  const onSelectionChangeRef = useRef(onSelectionChange)
  onSelectionChangeRef.current = onSelectionChange

  const isInternalUpdateRef = useRef(false)
  const prevSelectionRef = useRef(selection)

  const validLabels = useMemo(
    () => new Set(options.map(opt => opt.label)),
    [options],
  )

  useEffect(() => {
    if (
      selection !== undefined &&
      !disabled &&
      !isInternalUpdateRef.current &&
      prevSelectionRef.current !== selection
    ) {
      const newIndices = new Set<number>()
      const usedIndices = new Set<number>()
      selection.forEach(label => {
        const index = options.findIndex((opt, idx) => opt.label === label && !usedIndices.has(idx))
        if (index !== -1) {
          newIndices.add(index)
          usedIndices.add(index)
        }
      })

      const currentArray = Array.from(selectedIndices)
      const hasChanges =
        newIndices.size !== currentArray.length ||
        currentArray.some(idx => !newIndices.has(idx))

      if (hasChanges) {
        setSelectedIndices(newIndices)
      }
    }
    prevSelectionRef.current = selection
    isInternalUpdateRef.current = false
  }, [selection, disabled, selectedIndices, options])

  const onActionRef = useRef(onAction)
  onActionRef.current = onAction

  const handleOptionToggle = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return
    const indexStr = event.currentTarget.dataset.index
    if (indexStr === undefined) return
    const index = parseInt(indexStr, 10)
    if (isNaN(index)) return

    const clickedOption = options[index]
    if (clickedOption?.action && onActionRef.current) {
      onActionRef.current(clickedOption.action)
      return
    }

    isInternalUpdateRef.current = true

    setSelectedIndices(prevSelected => {
      const newSelection = new Set(prevSelected)
      if (select === 'single') {
        // For single select, toggle if already selected, otherwise clear and select
        if (newSelection.has(index)) {
          newSelection.delete(index)
        } else {
          newSelection.clear()
          newSelection.add(index)
        }
      } else {
        if (newSelection.has(index)) {
          newSelection.delete(index)
        } else {
          newSelection.add(index)
        }
      }

      const selectedLabels = Array.from(newSelection).map(idx => options[idx]?.label).filter(Boolean)

      const otherSelections = (selection ?? []).filter(s => !validLabels.has(s))
      const mergedSelection = [...otherSelections, ...selectedLabels]

      onSelectionChangeRef.current(mergedSelection)
      return newSelection
    })
  }, [disabled, select, selection, validLabels, options])

  if (options.length === 0) {
    return undefined
  }

  return (
    <PillsContainer>
      {options.map((option, index) => {
        const isSelected = selectedIndices.has(index)
        return (
          <StyledFilterChip
            key={index}
            size="medium"
            selected={isSelected}
            disabled={disabled}
            onClick={handleOptionToggle}
            data-index={index}
          >
            {option.label}
          </StyledFilterChip>
        )
      })}
    </PillsContainer>
  )
}

export default PillsList
